import { useEffect, useState, useRef } from "react";
import { useAuth } from "../lib/auth";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

const PRESETS = [
  { name: "Amdir Teal", primary: "#0F3D3A", accent: "#E8935C" },
  { name: "Amdir Green", primary: "#1A4D2E", accent: "#111111" },
  { name: "Electric Blue", primary: "#0A5AFF", accent: "#111111" },
  { name: "Ochre", primary: "#B45309", accent: "#111111" },
  { name: "Studio Black", primary: "#111111", accent: "#111111" },
  { name: "Burgundy", primary: "#7C1D1D", accent: "#111111" },
];

export default function BrandSettings() {
  const { brand, refreshBrand, applyBrand } = useAuth();
  const [f, setF] = useState({ studio_name: "", tagline: "", logo_url: "", primary_color: "#0F3D3A", accent_color: "#E8935C" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (brand) setF({
      studio_name: brand.studio_name || "",
      tagline: brand.tagline || "",
      logo_url: brand.logo_url || "",
      primary_color: brand.primary_color || "#1A4D2E",
      accent_color: brand.accent_color || "#111111",
    });
  }, [brand?.id]);

  const preview = (patch) => {
    const next = { ...f, ...patch };
    setF(next);
    applyBrand({ ...brand, ...next });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/brand", f);
      await refreshBrand();
      toast.success("Brand aggiornato");
    } catch (e) { toast.error("Errore"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const t = localStorage.getItem("crm_token");
      const r = await fetch(`${API}/brand/logo`, { method: "POST", headers: { Authorization: `Bearer ${t}` }, body: fd });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail || "err"); }
      const data = await r.json();
      preview({ logo_url: data.logo_url });
      await refreshBrand();
      toast.success("Logo caricato");
    } catch (e) { toast.error(e.message || "Errore upload"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = () => preview({ logo_url: "" });

  return (
    <div className="p-8 lg:p-10 max-w-4xl space-y-6 fade-up">
      <header>
        <div className="mono-label">Personalizzazione</div>
        <h1 className="font-display text-4xl font-medium mt-1">Brand Identity</h1>
        <p className="text-sm text-neutral-500 mt-2">Personalizza il CRM con il tuo studio: nome, logo, colore. Anteprima live.</p>
      </header>

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-neutral-200 bg-white p-6 space-y-4">
          <div className="mono-label mb-2">Identità</div>
          <div><Label>Nome studio</Label><Input data-testid="input-studio-name" value={f.studio_name} onChange={(e) => preview({ studio_name: e.target.value })} /></div>
          <div><Label>Payoff / tagline</Label><Input value={f.tagline} onChange={(e) => preview({ tagline: e.target.value })} /></div>
          <div>
            <Label>Logo</Label>
            <div className="mt-2 flex items-start gap-3">
              <div className="h-20 w-20 border border-neutral-200 flex items-center justify-center bg-neutral-50">
                {f.logo_url ? (
                  <img src={f.logo_url} alt="logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="mono-label">no logo</span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-file-input" />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-btn">
                    <Upload className="h-3.5 w-3.5 mr-1.5" /> {uploading ? "Caricamento..." : "Carica logo"}
                  </Button>
                  {f.logo_url && (
                    <Button type="button" variant="ghost" size="sm" onClick={removeLogo} data-testid="logo-remove-btn">
                      <X className="h-3.5 w-3.5 mr-1" /> Rimuovi
                    </Button>
                  )}
                </div>
                <Input placeholder="oppure incolla URL..." value={f.logo_url && !f.logo_url.startsWith("data:") ? f.logo_url : ""} onChange={(e) => preview({ logo_url: e.target.value })} />
              </div>
            </div>
          </div>
        </div>

        <div className="border border-neutral-200 bg-white p-6 space-y-4">
          <div className="mono-label mb-2">Colori</div>
          <div className="flex items-center gap-3">
            <Input type="color" className="w-16 h-10 p-1" value={f.primary_color} onChange={(e) => preview({ primary_color: e.target.value })} data-testid="input-primary-color" />
            <Input value={f.primary_color} onChange={(e) => preview({ primary_color: e.target.value })} className="font-mono" />
          </div>
          <div>
            <div className="mono-label mb-2">Preset</div>
            <div className="grid grid-cols-3 gap-2">
              {PRESETS.map((p) => (
                <button key={p.name} type="button" onClick={() => preview({ primary_color: p.primary, accent_color: p.accent })}
                  data-testid={`preset-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                  className="border border-neutral-200 p-2 flex items-center gap-2 hover:border-neutral-400">
                  <div className="h-4 w-4" style={{ background: p.primary }} />
                  <span className="text-xs">{p.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 border border-neutral-200 bg-white p-6">
          <div className="mono-label mb-3">Anteprima</div>
          <div className="p-8 border border-neutral-200 grid-lines">
            <div className="flex items-center gap-4 mb-4">
              {f.logo_url ? <img src={f.logo_url} className="h-12 w-12 object-contain" /> : <div className="h-12 w-12 brand-bg text-white font-display font-bold text-2xl flex items-center justify-center">{f.studio_name?.[0] || "S"}</div>}
              <div>
                <div className="font-display text-2xl font-medium">{f.studio_name || "Studio"}</div>
                <div className="mono-label">{f.tagline || "Payoff dello studio"}</div>
              </div>
            </div>
            <button type="button" className="brand-bg text-white px-4 py-2 text-sm">Pulsante brand</button>
          </div>
        </div>

        <Button type="submit" data-testid="save-brand" className="brand-bg hover:opacity-90 lg:col-span-2">Salva impostazioni brand</Button>
      </form>
    </div>
  );
}
